/**
 * Invoice PTC — layout factura comercial (sin bordes de grilla, sin marca de página).
 * Líneas de mercancía desde datos_formulario.lineasMercancia (captura packing list + PI).
 */
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import ptcImage from '../../imagenes/ptc.jpg';
import firmaPtcImage from '../../imagenes/firma-ptc.png';
import {
  buildInvoicePtcMerchandise,
  packingListTotals,
  usdAmountInWords,
} from './invoicePtcMerchandise';
import { invoiceNumberCorrelativo } from './invoiceNumberUtils';

/** TOTAL DUE en letras (formato factura PTC: …, 60/100 USD DOLLARS). */
function totalDueWordsPtc(amount) {
  const n = Math.round(Number(amount) * 100) / 100;
  if (!Number.isFinite(n) || n <= 0) return '';
  const whole = Math.floor(n);
  const cents = Math.round((n - whole) * 100);
  const words = usdAmountInWords(whole)
    .replace(/\s+DOLLARS?$/i, '')
    .trim();
  return `${words}, ${String(cents).padStart(2, '0')}/100 USD DOLLARS`;
}

const RUT_EMPRESA = '77.710.540-K';
const SII_OFICINA = 'S.I.I. SANTIAGO ORIENTE';
const COMPANY_LINES = [
  'PALM TRADE CHILE SPA',
  'Avenida Presidente Kennedy 5146, oficina 51',
  'Vitacura, Santiago, Chile',
  'Fono: (56-2) 22190119 - Fax: (56-2) 22184390',
];

const tableGrid = {
  theme: 'grid',
  styles: {
    fontSize: 7,
    cellPadding: { top: 1.2, right: 1.2, bottom: 1.2, left: 1.2 },
    lineColor: [0, 0, 0],
    lineWidth: 0.15,
    textColor: [0, 0, 0],
    valign: 'middle',
    halign: 'center',
  },
  headStyles: {
    fontSize: 7,
    fontStyle: 'bold',
    fillColor: [255, 255, 255],
    textColor: [0, 0, 0],
    halign: 'center',
    valign: 'middle',
  },
};

/** Proporciones fila embarque (Vessel…): 35 / 25 / 20 / 20 */
const col4Vessel = (w) => ({
  0: { cellWidth: w * 0.35 },
  1: { cellWidth: w * 0.25 },
  2: { cellWidth: w * 0.2 },
  3: { cellWidth: w * 0.2 },
});

/** Proporciones fila comercial + peso: 23 / 29 / 28 / 10 / 10 (últimos 20 % = Total Weight) */
const col5SalesWeight = (w) => ({
  0: { cellWidth: w * 0.23 },
  1: { cellWidth: w * 0.29 },
  2: { cellWidth: w * 0.28 },
  3: { cellWidth: w * 0.1 },
  4: { cellWidth: w * 0.1 },
});

/** Mercancía: Internal 12 / Qty 12 / Desc 35 / N° 5 / Date 8 / Curr 8 / Unit 10 / Total 10 */
const col8Merchandise = (w) => ({
  0: { cellWidth: w * 0.12 },
  1: { cellWidth: w * 0.12 },
  2: { cellWidth: w * 0.35, halign: 'left' },
  3: { cellWidth: w * 0.05 },
  4: { cellWidth: w * 0.08 },
  5: { cellWidth: w * 0.08 },
  6: { cellWidth: w * 0.1, halign: 'right' },
  7: { cellWidth: w * 0.1, halign: 'right' },
});

function priceCellPdf(v) {
  if (v == null || v === '' || Number.isNaN(Number(v))) return '';
  return fmtNum(Number(v), 2);
}

const merchHeadLabel = { fontStyle: 'bold', halign: 'center', valign: 'middle' };
const merchHeadCaps = { ...merchHeadLabel, fontStyle: 'bold' };

function safe(s) {
  return s == null ? '' : String(s);
}

function upper(s) {
  const t = safe(s).trim();
  return t ? t.toUpperCase() : '';
}

function fmtNum(v, dec = 2) {
  if (v == null || v === '' || Number.isNaN(Number(v))) return '';
  return new Intl.NumberFormat('es-CL', {
    minimumFractionDigits: dec,
    maximumFractionDigits: dec,
  }).format(Number(v));
}

function fmtDateLongEn(isoDate) {
  if (!isoDate) return '';
  const d = new Date(isoDate + (isoDate.length === 10 ? 'T12:00:00' : ''));
  if (Number.isNaN(d.getTime())) return safe(isoDate);
  return d.toLocaleDateString('en-US', { day: 'numeric', month: 'long', year: 'numeric' });
}

function fmtDateDjc(isoDate) {
  if (!isoDate) return '';
  const d = new Date(isoDate + (isoDate.length === 10 ? 'T12:00:00' : ''));
  if (Number.isNaN(d.getTime())) return '';
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  return `${dd}-${mm}-${d.getFullYear()}`;
}

function parseDireccionCliente(texto, pinCode) {
  const lines = safe(texto)
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);
  const sinPin = lines.filter((l) => !/^PIN CODE\s*:/i.test(l));
  return {
    address: sinPin[0] || '',
    extra: sinPin.slice(1).join(', '),
    postal: pinCode || '',
  };
}

function descriptionForPdf(ln) {
  const raw = safe(ln.descriptionBlock || ln.productName).trim();
  if (!raw) return '';
  return raw
    .split(/\r?\n/)
    .map((line) => upper(line))
    .join('\n');
}

export function mapLineasMercanciaToPdfRows(lineasMercancia, inv, f) {
  const numero = invoiceNumberCorrelativo(
    inv?.numero_invoice || f?.numeroIdentificadorPtc || ''
  );
  const fecha = inv?.fecha_invoice || f?.fechaInvoice || f?.fechaEmbarque || '';
  const currency = safe(f?.invoiceCurrency || 'USD').toUpperCase() || 'USD';
  return (lineasMercancia || []).map((ln) => {
    const qty =
      ln.quantityM3 != null && !Number.isNaN(Number(ln.quantityM3))
        ? Number(ln.quantityM3)
        : null;
    const unit =
      ln.unitPriceUsd != null && !Number.isNaN(Number(ln.unitPriceUsd))
        ? Number(ln.unitPriceUsd)
        : null;
    let total =
      ln.totalValueUsd != null && !Number.isNaN(Number(ln.totalValueUsd))
        ? Number(ln.totalValueUsd)
        : null;
    if (total == null && qty != null && unit != null) {
      total = Math.round(qty * unit * 100) / 100; // M³ × USD/m³ (PI), máx. 2 dec.
    }
    return {
      internalCode: '',
      quantity: qty != null ? fmtNum(qty, 3) : '',
      description: descriptionForPdf(ln),
      productName: ln.productName,
      djcNum: invoiceNumberCorrelativo(ln.djcNum) || numero,
      djcDate: fmtDateDjc(ln.djcDate || fecha),
      currency: safe(ln.currency || currency).toUpperCase() || 'USD',
      unitPrice: unit,
      totalValue: total,
    };
  });
}

function lineasFromFormulario(f, inv) {
  const saved = f?.lineasMercancia;
  if (Array.isArray(saved) && saved.length) {
    return mapLineasMercanciaToPdfRows(saved, inv, f);
  }
  return [];
}

/** Reconstruye líneas de mercancía desde packing list + PI (última del PL). */
export async function lineasMercanciaFromPackingList(pl, proforma, inv, f) {
  const merc = buildInvoicePtcMerchandise(pl?.lineas || [], proforma, {
    currency: f?.invoiceCurrency || 'USD',
    fechaInvoice: inv?.fecha_invoice || f?.fechaInvoice,
    fechaEmbarque: pl?.fecha_embarque || f?.fechaEmbarque,
    numeroInvoice: inv?.numero_invoice || f?.numeroIdentificadorPtc || '',
    productDescriptionSnapshot:
      pl?.product_description_snapshot || f?.productDescriptionSnapshot || '',
  });
  return {
    lineas: mapLineasMercanciaToPdfRows(merc.lineasMercancia, inv, f),
    totalBundles: merc.totalBundles,
    totalM3: merc.totalM3,
    totalUsd: merc.totalUsd,
  };
}

function piLabelUpper(pi) {
  if (!pi) return '';
  const idPi = pi.id_pi != null && String(pi.id_pi).trim();
  return (idPi ? String(idPi) : `PI #${pi.id_proforma}`).toUpperCase();
}

/** PI del listado cargado en pantalla (Contract No. / ids seleccionados). */
export function proformaFromLista(proformas, idProformas, contractNo) {
  const lista = proformas || [];
  const ids = idProformas || [];
  if (ids.length) {
    const lastId = ids[ids.length - 1];
    const found = lista.find((p) => String(p.id_proforma) === String(lastId));
    if (found) return found;
  }
  const c = safe(contractNo).trim().toUpperCase();
  if (!c) return null;
  return (
    lista.find((p) => piLabelUpper(p) === c) ||
    lista.find((p) => `PI #${p.id_proforma}`.toUpperCase() === c) ||
    null
  );
}

export function lineasPdfTienenPrecios(lineas) {
  return (lineas || []).some((ln) => {
    const p = ln.unitPrice ?? ln.unitPriceUsd;
    return p != null && p !== '' && !Number.isNaN(Number(p));
  });
}

/** IDs de PI: packing list, o Contract No. guardado en el formulario. */
export function resolveProformaIds(pl, datosFormulario) {
  const fromPl = pl?.id_proformas;
  if (Array.isArray(fromPl) && fromPl.length) return fromPl;
  const fromForm = datosFormulario?.idsProformaSeleccion;
  if (Array.isArray(fromForm) && fromForm.length) return fromForm;
  return [];
}

export async function fetchOrdenCompraById(apiBase, headers, idOrdenCompra) {
  if (!idOrdenCompra || !apiBase || !headers) return null;
  const res = await fetch(`${apiBase}/ordenescompra`, { headers });
  if (!res.ok) return null;
  const all = await res.json();
  return (
    all.find((o) => String(o.id_ordencompra) === String(idOrdenCompra)) || null
  );
}

export async function fetchProformaUltima(apiBase, headers, idProformas, proformaEnMemoria = null) {
  const ids = idProformas || [];
  if (!ids.length) return proformaEnMemoria || null;
  const lastId = ids[ids.length - 1];
  if (
    proformaEnMemoria &&
    String(proformaEnMemoria.id_proforma) === String(lastId)
  ) {
    return proformaEnMemoria;
  }
  if (!apiBase || !headers) return proformaEnMemoria || null;
  const res = await fetch(`${apiBase}/proformas`, { headers });
  if (!res.ok) return proformaEnMemoria || null;
  const all = await res.json();
  return (
    all.find((p) => String(p.id_proforma) === String(lastId)) ||
    proformaEnMemoria ||
    null
  );
}

/**
 * Params PDF desde detalle (packing list) + PI ya cargada en pantalla (Contract No.).
 * Evita depender solo del API cuando la PI está en el formulario.
 */
export function invoicePtcParamsFromFormAndDetail(
  datosFormulario,
  plLineas,
  proforma,
  overrides = {}
) {
  const f = datosFormulario || {};
  const inv = {
    numero_invoice: overrides.numeroInvoice ?? f.numeroIdentificadorPtc ?? '',
    fecha_invoice: f.fechaInvoice,
    datos_formulario: f,
  };
  const merc = buildInvoicePtcMerchandise(plLineas || [], proforma, {
    currency: f.invoiceCurrency || 'USD',
    fechaInvoice: f.fechaInvoice,
    fechaEmbarque: f.fechaEmbarque,
    numeroInvoice: inv.numero_invoice,
    productDescriptionSnapshot: f.productDescriptionSnapshot || '',
  });
  const lineasPdf = mapLineasMercanciaToPdfRows(merc.lineasMercancia, inv, f);
  const base = invoicePtcParamsFromForm(datosFormulario, overrides);
  const tienePrecios = lineasPdfTienenPrecios(lineasPdf);
  const valorUsd =
    merc.totalUsd > 0
      ? merc.totalUsd
      : overrides.valorUsd != null && Number(overrides.valorUsd) > 0
        ? Number(overrides.valorUsd)
        : f.totalUsd > 0
          ? Number(f.totalUsd)
          : base.valorUsd;

  return {
    ...base,
    lineas: tienePrecios ? lineasPdf : base.lineas,
    valorUsd,
    totalBundles: merc.totalBundles ?? f.totalBundles ?? base.totalBundles,
    totalM3: merc.totalM3 ?? f.totalM3 ?? base.totalM3,
  };
}

/**
 * Params PDF PTC: datos guardados + mercancía recalculada desde packing list asociado.
 */
export async function invoicePtcParamsFromApiWithPackingList(
  inv,
  { apiBase, headers, proformaEnMemoria = null }
) {
  const base = invoicePtcParamsFromApi(inv);
  const idPl = inv?.id_packing_list;
  const f = inv?.datos_formulario || {};
  if (!idPl || !apiBase || !headers) return base;

  try {
    const plRes = await fetch(`${apiBase}/packinglists/${idPl}`, { headers });
    if (!plRes.ok) return base;
    const pl = await plRes.json();
    const idProformas = resolveProformaIds(pl, f);
    let proforma = await fetchProformaUltima(
      apiBase,
      headers,
      idProformas,
      proformaEnMemoria
    );
    if (!proforma && pl.id_ordencompra) {
      proforma = await fetchOrdenCompraById(apiBase, headers, pl.id_ordencompra);
    }
    const merc = await lineasMercanciaFromPackingList(pl, proforma, inv, f);
    const plTotals = packingListTotals(pl.lineas || []);

    const lineasApi = merc.lineas;
    const lineasConPrecio = lineasApi.filter((ln) => lineasPdfTienenPrecios([ln]));
    const lineasForm = base.lineas.filter((ln) => lineasPdfTienenPrecios([ln]));
    const lineas =
      lineasConPrecio.length > 0
        ? lineasApi
        : lineasForm.length > 0
          ? lineasForm
          : lineasApi.length
            ? lineasApi
            : base.lineas;

    const totalUsd =
      merc.totalUsd > 0
        ? merc.totalUsd
        : inv?.valor_usd != null && Number(inv.valor_usd) > 0
          ? Number(inv.valor_usd)
          : f?.totalUsd > 0
            ? Number(f.totalUsd)
            : base.valorUsd;

    return {
      ...base,
      lineas,
      totalBundles: merc.totalBundles ?? plTotals.totalBundles ?? base.totalBundles,
      totalM3: merc.totalM3 ?? plTotals.totalM3 ?? base.totalM3,
      valorUsd: totalUsd,
      nombreBl: base.nombreBl || pl.nombre_bl || '',
      nombreNave: base.nombreNave || pl.nombre_nave || '',
      paisOrigen: base.paisOrigen || pl.pais_origen || '',
      paisDestino: base.paisDestino || pl.pais_destino || '',
      puertoOrigen: base.puertoOrigen || pl.nombre_puerto_origen || '',
      puertoDestino: base.puertoDestino || pl.nombre_puerto_destino || '',
      lugarEntrega: base.lugarEntrega || pl.lugar_entrega || '',
    };
  } catch (err) {
    console.warn('invoicePtcParamsFromApiWithPackingList:', err);
    return base;
  }
}

/**
 * Vista previa / crear invoice: mismos datos de mercancía que al descargar (packing list + PI).
 */
export async function invoicePtcParamsFromFormWithPackingList(
  datosFormulario,
  overrides = {},
  { apiBase, headers, idPackingList, proformaEnMemoria = null, plLineas = null }
) {
  const f = datosFormulario || {};
  const inv = {
    id_packing_list: idPackingList,
    numero_invoice: overrides.numeroInvoice ?? f.numeroIdentificadorPtc ?? '',
    fecha_invoice: f.fechaInvoice,
    nombre_cliente: f.clienteNombre,
    nombre_bl: f.nombreBl,
    pais_destino: f.paisDestino,
    valor_usd: null,
    contrato_pi: f.contractNo,
    total_m3: f.totalM3,
    datos_formulario: f,
  };
  const sync = invoicePtcParamsFromFormAndDetail(
    f,
    plLineas,
    proformaEnMemoria,
    overrides
  );
  if (lineasPdfTienenPrecios(sync.lineas)) return sync;
  return invoicePtcParamsFromApiWithPackingList(inv, {
    apiBase,
    headers,
    proformaEnMemoria,
  });
}

/** Datos del formulario crear invoice → mismos params que al descargar. */
export function invoicePtcParamsFromForm(datosFormulario, overrides = {}) {
  const f = datosFormulario || {};
  const valorUsd =
    overrides.valorUsd != null
      ? overrides.valorUsd
      : f.totalUsd != null
        ? Number(f.totalUsd)
        : null;
  return invoicePtcParamsFromApi({
    numero_invoice: overrides.numeroInvoice ?? f.numeroIdentificadorPtc ?? '',
    fecha_invoice: f.fechaInvoice,
    nombre_cliente: f.clienteNombre,
    nombre_bl: f.nombreBl,
    pais_destino: f.paisDestino,
    valor_usd: valorUsd,
    contrato_pi: f.contractNo,
    total_m3: f.totalM3,
    datos_formulario: f,
  });
}

/** Respuesta GET /commercialinvoices/:id → params para un único PDF PTC. */
export function invoicePtcParamsFromApi(inv) {
  const f = inv?.datos_formulario || {};
  const lineas = lineasFromFormulario(f, inv);
  const sumProducts =
    lineas.reduce((s, ln) => s + (Number(ln.totalValue) || 0), 0) || null;
  const totalUsd =
    inv?.valor_usd != null
      ? Number(inv.valor_usd)
      : f?.totalUsd != null
        ? Number(f.totalUsd)
        : sumProducts;

  return {
    numeroInvoice: inv?.numero_invoice || f?.numeroIdentificadorPtc || '',
    fechaInvoice: inv?.fecha_invoice || f?.fechaInvoice,
    issueCity: 'Santiago',
    clienteNombre: inv?.nombre_cliente || f?.clienteNombre || '',
    clienteDireccion: f?.clienteDireccion || '',
    taxId: f?.taxId || '',
    pinCode: f?.pinCode || '',
    paisOrigen: f?.paisOrigen || '',
    paisDestino: inv?.pais_destino || f?.paisDestino || '',
    puertoOrigen: f?.puertoOrigen || '',
    puertoDestino: f?.puertoDestino || '',
    lugarEntrega: f?.lugarEntrega || '',
    nombreNave: f?.nombreNave || '',
    nombreBl: inv?.nombre_bl || f?.nombreBl || '',
    contractNo: inv?.contrato_pi || f?.contractNo || '',
    purchaseOrder: f?.purchaseOrder || '',
    salesTerms: f?.salesTerms || 'AFIRME',
    incoterms: f?.incoterms || '',
    paymentTerms: f?.paymentTermsPtc || '',
    netWeight: f?.netWeight,
    grossWeight: f?.grossWeight,
    valorUsd: totalUsd,
    totalBundles: f?.totalBundles,
    totalM3: f?.totalM3 ?? inv?.total_m3,
    lineas,
  };
}

/** Bloque cliente izquierdo con interlineado compacto (sin autoTable). */
function drawLeftCustomerBlock(doc, opts) {
  const {
    startX,
    startY,
    blockW,
    labelW,
    nombre,
    addrLines,
    cityCountry,
    postal,
  } = opts;
  const padX = 1.5;
  const padY = 1.5;
  const fontSize = 8;
  const lineH = 2.8;
  const labelX = startX + padX;
  const valueX = startX + labelW + padX * 0.5;
  const valueW = blockW - labelW - padX * 2.5;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(fontSize);

  let cy = startY + padY + 3;

  const row = (label, value) => {
    doc.text(label, labelX, cy);
    doc.text(value || '', valueX, cy);
    cy += lineH;
  };

  row('Mr./Mrs.:', nombre);

  let addressStarted = false;
  const linesToDraw = addrLines.length ? addrLines : [''];
  linesToDraw.forEach((line) => {
    const wrapped = doc.splitTextToSize(line || '', valueW);
    wrapped.forEach((part) => {
      if (!addressStarted) {
        doc.text('Address:', labelX, cy);
        addressStarted = true;
      }
      doc.text(part, valueX, cy);
      cy += lineH;
    });
  });
  if (!addressStarted) {
    doc.text('Address:', labelX, cy);
    cy += lineH;
  }

  row('State:', '');
  row('City/Country:', cityCountry);
  row('Phone:', '');
  row('Fax:', '');
  row('Postal Code/Pin Code:', postal);

  return cy + padY;
}

const RIGHT_LOGISTICS_ROWS = 6;

/** Tabla origen/destino/puertos; altura total = blockHeight (misma que bloque cliente). */
function drawRightLogisticsBlock(doc, opts) {
  const { startY, x, width, params, blockHeight } = opts;
  const baseRowH = Math.floor((blockHeight / RIGHT_LOGISTICS_ROWS) * 100) / 100;
  const lastRowH = Math.round((blockHeight - baseRowH * (RIGHT_LOGISTICS_ROWS - 1)) * 100) / 100;

  autoTable(doc, {
    startY,
    margin: { left: x, right: doc.internal.pageSize.getWidth() - x - width },
    tableWidth: width,
    theme: 'grid',
    styles: {
      fontSize: 8,
      cellPadding: { top: 1.2, right: 1.2, bottom: 1.2, left: 1.2 },
      lineColor: [0, 0, 0],
      lineWidth: 0.15,
      textColor: [0, 0, 0],
      valign: 'middle',
      halign: 'center',
    },
    didParseCell(data) {
      if (data.section === 'body') {
        const isLast = data.row.index === RIGHT_LOGISTICS_ROWS - 1;
        data.cell.height = isLast ? lastRowH : baseRowH;
        if (isLast && opts.closeBottomAt) {
          data.cell.styles.lineWidth = {
            top: 0.15,
            right: 0.15,
            bottom: 0,
            left: 0.15,
          };
        }
      }
    },
    body: [
      [
        { content: 'County of Origin', styles: { fontSize: 7 } },
        { content: 'Destination Country', styles: { fontSize: 7 } },
      ],
      [
        { content: upper(params.paisOrigen) },
        { content: upper(params.paisDestino) },
      ],
      [
        { content: 'Loading Port', styles: { fontSize: 7 } },
        { content: 'Unloading Port', styles: { fontSize: 7 } },
      ],
      [
        { content: upper(params.puertoOrigen) },
        { content: upper(params.puertoDestino) },
      ],
      [
        { content: 'Place of delivery', colSpan: 2, styles: { fontSize: 7 } },
      ],
      [
        {
          content: upper(params.lugarEntrega || params.puertoDestino),
          colSpan: 2,
        },
      ],
    ],
    columnStyles: {
      0: { cellWidth: width / 2 },
      1: { cellWidth: width / 2 },
    },
  });
  const finalY = doc.lastAutoTable?.finalY ?? startY + blockHeight;
  return {
    finalY,
    left: x,
    right: x + width,
  };
}

/** Cierra borde inferior y esquina inferior derecha de la tabla logística. */
function sealRightLogisticsBottom(doc, tableBox, blockEndY) {
  const { left, right, finalY } = tableBox;
  doc.setDrawColor(0, 0, 0);
  doc.setLineWidth(0.15);
  doc.line(left, blockEndY, right, blockEndY);
  if (finalY < blockEndY - 0.05) {
    doc.line(right, finalY, right, blockEndY);
  }
}

/** Altura reservada al pie (firma fija + margen de tablas). A4 mm. */
const PTC_SIGNATURE_BOTTOM_PAD = 10;
const PTC_SIGNATURE_BLOCK_H = 15 + 2 + 9;
const PTC_SIGNATURE_ZONE_H = PTC_SIGNATURE_BOTTOM_PAD + PTC_SIGNATURE_BLOCK_H;
const PTC_MERCH_HEAD_EST_H = 11;

function ptcSignatureZoneTop(doc) {
  return doc.internal.pageSize.getHeight() - PTC_SIGNATURE_ZONE_H;
}

function estimateMerchBodyHeightMm(doc, bodyRows, contentW) {
  const descColW = contentW * 0.35;
  let h = 0;
  for (const row of bodyRows) {
    if (row[0]?.colSpan === 8) continue;
    const desc = typeof row[2] === 'object' ? row[2].content : row[2];
    const lines = doc.splitTextToSize(String(desc || ''), descColW - 2);
    h += Math.max(5, 2 + lines.length * 3.2);
  }
  return h;
}

const merchBodyVertLines = { top: 0, right: 0.15, bottom: 0, left: 0.15 };

/** Fila vacía (8 celdas) para mantener líneas verticales en el espacio de relleno. */
function merchFillerRowCells(fillerH) {
  return Array.from({ length: 8 }, (_, col) => ({
    content: '',
    styles: {
      minCellHeight: col === 0 ? fillerH : undefined,
      valign: 'top',
      lineWidth: merchBodyVertLines,
    },
  }));
}

/** Fila vacía al final del detalle para ocupar el espacio hasta totales + firma. */
function merchBodyWithPageFiller(doc, bodyRows, opts) {
  const { merchStartY, contentW, footerBelowMerchH } = opts;
  const zoneTop = ptcSignatureZoneTop(doc);
  const natural = estimateMerchBodyHeightMm(doc, bodyRows, contentW);
  const need = merchStartY + PTC_MERCH_HEAD_EST_H + natural + footerBelowMerchH;
  if (need > zoneTop) return bodyRows;
  const fillerH = zoneTop - merchStartY - PTC_MERCH_HEAD_EST_H - footerBelowMerchH - natural;
  if (fillerH <= 2) return bodyRows;
  return [...bodyRows, merchFillerRowCells(fillerH)];
}

/** Firma fija al pie de la última página (independiente del alto del contenido). */
function drawPtcSignatureAtPageBottom(doc, pageW, margin) {
  const pageH = doc.internal.pageSize.getHeight();
  const pageNo = doc.getNumberOfPages();
  doc.setPage(pageNo);

  const sigImgW = 40;
  const sigImgH = 15;
  const sigBlockW = 48;
  const imgY = pageH - PTC_SIGNATURE_ZONE_H;
  const sigCenterX = pageW - margin - sigBlockW / 2;
  const imgX = sigCenterX - sigImgW / 2;

  doc.addImage(firmaPtcImage, 'PNG', imgX, imgY, sigImgW, sigImgH);
  const lineY = imgY + sigImgH + 2;
  doc.setDrawColor(0, 0, 0);
  doc.setLineWidth(0.2);
  doc.line(pageW - margin - sigBlockW + 2, lineY, pageW - margin - 2, lineY);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(6.5);
  doc.text('SIGNATURE', sigCenterX, lineY + 3.2, { align: 'center' });
  doc.setFontSize(7);
  doc.text('p.p. PALM TRADE CHILE SPA', sigCenterX, lineY + 6.8, { align: 'center' });
}

/**
 * Construye el documento jsPDF Invoice PTC (sin descargar).
 */
export function buildInvoicePtcPdfDoc(params) {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();
  const margin = 10;
  const contentW = pageW - 2 * margin;
  /** Espacio reservado al pie para que tablas no tapen la firma fija. */
  const tableMargin = { left: margin, right: margin, bottom: PTC_SIGNATURE_ZONE_H };
  const logoY = 0;
  const boxY = 6;
  let y = boxY;

  const logoW = 34;
  const imgProps = doc.getImageProperties(ptcImage);
  const logoH = (imgProps.height * logoW) / imgProps.width;
  doc.addImage(ptcImage, 'JPEG', margin, logoY, logoW, logoH);

  const infoX = margin + 2;
  let infoY = logoY + logoH + 2;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7);
  COMPANY_LINES.forEach((ln) => {
    doc.text(ln, infoX, infoY);
    infoY += 3.2;
  });

  const boxW = 60;
  const boxX = pageW - margin - boxW;
  autoTable(doc, {
    startY: boxY,
    margin: { left: boxX, right: margin },
    tableWidth: boxW,
    theme: 'plain',
    styles: {
      fontSize: 7,
      cellPadding: { top: 1.4, right: 0.0, bottom: 1.4, left: 0.0 },
      lineColor: [0, 0, 0],
      lineWidth: 0,
      textColor: [0, 0, 0],
      valign: 'middle',
      halign: 'center',
    },
    body: [
      [{ content: `R.U.T: ${RUT_EMPRESA}`, styles: { fontSize: 9, fontStyle: 'bold' } }],
      [
        {
          content: 'FACTURA DE VENTA DE MERCADERIAS\nSITUADAS EN EL EXTRANJERO',
          styles: { fontSize: 7, fontStyle: 'normal' },
        },
      ],
      [
        {
          content: `INVOICE N° ${invoiceNumberCorrelativo(params.numeroInvoice) || '—'}`,
          styles: { fontSize: 10, fontStyle: 'bold' },
        },
      ],
    ],
  });

  const yBoxEnd = doc.lastAutoTable.finalY;
  // Solo borde externo del bloque (sin líneas internas).
  doc.setDrawColor(0, 0, 0);
  doc.setLineWidth(0.2);
  doc.rect(boxX, boxY, boxW, yBoxEnd - boxY);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7);
  doc.text(SII_OFICINA, boxX + boxW / 2, yBoxEnd + 3.8, { align: 'center' });

  y = Math.max(infoY + 1, yBoxEnd + 5.2);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.text(
    `ISSUE DATE: ${params.issueCity || 'Santiago'}, ${fmtDateLongEn(params.fechaInvoice)}`,
    margin,
    y + 3.5
  );
  y += 6;

  const leftW = contentW * 0.62;
  const rightW = contentW - leftW;
  const leftLabelW = leftW * 0.37;
  const addrLines = safe(params.clienteDireccion)
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean)
    .filter((l) => !/^PIN CODE\s*:/i.test(l))
    .map((l) => upper(l));
  const cityCountry = upper(params.paisDestino);
  const postal = safe(params.pinCode).trim();
  const blockStartY = y;
  const leftEndY = drawLeftCustomerBlock(doc, {
    startX: margin,
    startY: blockStartY,
    blockW: leftW,
    labelW: leftLabelW,
    nombre: upper(params.clienteNombre),
    addrLines,
    cityCountry,
    postal,
  });
  const blockH = leftEndY - blockStartY;
  const rightX = margin + leftW;

  const rightTable = drawRightLogisticsBlock(doc, {
    startY: blockStartY,
    x: rightX,
    width: rightW,
    params,
    blockHeight: blockH,
    closeBottomAt: leftEndY,
  });
  const blockEndY = Math.max(leftEndY, rightTable.finalY);
  sealRightLogisticsBottom(doc, rightTable, blockEndY);
  doc.setDrawColor(0, 0, 0);
  doc.setLineWidth(0.15);
  doc.rect(margin, blockStartY, leftW, blockEndY - blockStartY);
  y = blockEndY;

  const labelCell = { fontStyle: 'bold', halign: 'center', valign: 'middle' };
  const netFmt = fmtNum(params.netWeight, 3);
  const grossFmt = fmtNum(params.grossWeight, 3);
  const paymentTxt = safe(params.paymentTerms);

  autoTable(doc, {
    startY: y,
    margin: tableMargin,
    tableWidth: contentW,
    ...tableGrid,
    body: [
      [
        { content: 'Vessel', styles: labelCell },
        { content: 'Contract No.', styles: labelCell },
        { content: 'Purchase Order', styles: labelCell },
        { content: 'Bill of Lading', styles: labelCell },
      ],
      [
        { content: upper(params.nombreNave) },
        { content: upper(params.contractNo) },
        { content: upper(params.purchaseOrder) },
        { content: upper(params.nombreBl) },
      ],
    ],
    columnStyles: col4Vessel(contentW),
  });
  y = doc.lastAutoTable.finalY;

  autoTable(doc, {
    startY: y,
    margin: tableMargin,
    tableWidth: contentW,
    ...tableGrid,
    body: [
      [
        { content: 'Sales Terms', styles: labelCell },
        { content: 'Incoterms', styles: labelCell },
        { content: 'Payment Terms', styles: labelCell },
        {
          content: 'TOTAL WEIGHT (kg)',
          colSpan: 2,
          styles: labelCell,
        },
      ],
      [
        {
          content: upper(params.salesTerms),
          rowSpan: 2,
          styles: { valign: 'middle' },
        },
        {
          content: upper(params.incoterms),
          rowSpan: 2,
          styles: { valign: 'middle' },
        },
        {
          content: paymentTxt,
          rowSpan: 2,
          styles: { halign: 'center', valign: 'middle', fontSize: 6.5 },
        },
        { content: 'Net Weight', styles: labelCell },
        { content: 'Gross Weight', styles: labelCell },
      ],
      [
        { content: netFmt, styles: { valign: 'middle' } },
        { content: grossFmt, styles: { valign: 'middle' } },
      ],
    ],
    columnStyles: col5SalesWeight(contentW),
  });
  y = doc.lastAutoTable.finalY;

  autoTable(doc, {
    startY: y,
    margin: tableMargin,
    tableWidth: contentW,
    ...tableGrid,
    body: [
      [
        {
          content: 'Please Remit Payment to PALM TRADE CHILE SPA',
          colSpan: 5,
          styles: { fontStyle: 'bold', halign: 'center', fontSize: 7.5, valign: 'middle' },
        },
      ],
    ],
    columnStyles: col5SalesWeight(contentW),
  });
  y = doc.lastAutoTable.finalY;

  const lineas = params.lineas || [];
  const bodyMerchBase = lineas.map((ln) => [
    safe(ln.internalCode),
    safe(ln.quantity),
    { content: safe(ln.description), styles: { valign: 'top' } },
    safe(ln.djcNum),
    safe(ln.djcDate),
    safe(ln.currency),
    priceCellPdf(ln.unitPrice),
    priceCellPdf(ln.totalValue),
  ]);
  const bodyMerchRows = bodyMerchBase.length
    ? bodyMerchBase
    : [['', '—', '—', '', '', 'USD', '', '']];

  const totalUsdNum = params.valorUsd != null ? Number(params.valorUsd) : null;
  const dueWords = totalUsdNum != null ? totalDueWordsPtc(totalUsdNum) : '';
  const dueText = dueWords ? `TOTAL DUE: ${dueWords}` : 'TOTAL DUE: —';
  const dueLines = doc.splitTextToSize(dueText, contentW - 4);
  const dueFooterH = Math.max(14, 6 + dueLines.length * 3.4 + 5);
  const footerBelowMerchH = 8 + dueFooterH + 2;
  const merchStartY = y;
  const bodyMerch = merchBodyWithPageFiller(doc, bodyMerchRows, {
    merchStartY,
    contentW,
    footerBelowMerchH,
  });

  autoTable(doc, {
    startY: y,
    margin: tableMargin,
    tableWidth: contentW,
    ...tableGrid,
    head: [
      [
        { content: 'Internal Code', rowSpan: 2, styles: merchHeadLabel },
        { content: 'Quantity', rowSpan: 2, styles: merchHeadLabel },
        {
          content: 'MERCHANDISE DESCRIPTION',
          rowSpan: 2,
          styles: merchHeadCaps,
        },
        { content: 'DJC', colSpan: 5, styles: merchHeadCaps },
      ],
      [
        { content: 'N°', styles: merchHeadLabel },
        { content: 'Date', styles: merchHeadLabel },
        { content: 'Currency', styles: merchHeadLabel },
        { content: 'UNIT PRICE', styles: merchHeadCaps },
        { content: 'TOTAL VALUE', styles: merchHeadCaps },
      ],
    ],
    body: bodyMerch,
    bodyStyles: {
      valign: 'top',
      halign: 'center',
      lineWidth: merchBodyVertLines,
    },
    columnStyles: col8Merchandise(contentW),
  });
  y = doc.lastAutoTable.finalY;

  const totalBundles = params.totalBundles;
  const totalM3 = params.totalM3;
  const totalUsdFmt = totalUsdNum != null ? fmtNum(totalUsdNum, 2) : '';

  const totalesIzq = `Total bundles: ${totalBundles != null ? fmtNum(totalBundles, 0) : '—'}  |  Total M³: ${totalM3 != null ? fmtNum(totalM3, 3) : '—'}`;

  autoTable(doc, {
    startY: y,
    margin: tableMargin,
    tableWidth: contentW,
    ...tableGrid,
    body: [
      [
        {
          content: totalesIzq,
          colSpan: 6,
          styles: { fontStyle: 'bold', fontSize: 7, halign: 'left' },
        },
        { content: 'Total USD', styles: { fontStyle: 'bold', halign: 'right' } },
        { content: totalUsdFmt, styles: { fontStyle: 'bold', halign: 'right' } },
      ],
    ],
    columnStyles: col8Merchandise(contentW),
  });
  y = doc.lastAutoTable.finalY;

  autoTable(doc, {
    startY: y,
    margin: tableMargin,
    tableWidth: contentW,
    ...tableGrid,
    body: [
      [
        {
          content: ' ',
          colSpan: 8,
          styles: { minCellHeight: dueFooterH, valign: 'top' },
        },
      ],
    ],
    columnStyles: col8Merchandise(contentW),
    didDrawCell(data) {
      if (data.section !== 'body' || data.row.index !== 0 || data.column.index !== 0) {
        return;
      }
      const { x, y: cellY, width } = data.cell;
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(7);
      doc.text(dueLines, x + 1.5, cellY + 4);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(6.5);
      doc.text('( IN LETTERS )', x + 1.5, cellY + 4 + dueLines.length * 3.4 + 1);
    },
  });

  drawPtcSignatureAtPageBottom(doc, pageW, margin);

  return doc;
}

export function invoicePtcPdfFileName(params) {
  return `Invoice_PTC_${safe(params.numeroInvoice).replace(/\W/g, '_') || 'preview'}.pdf`;
}

/** Blob URL para vista previa en iframe (revocar con URL.revokeObjectURL al cerrar). */
export function createInvoicePtcPdfBlobUrl(params) {
  const doc = buildInvoicePtcPdfDoc(params);
  const blob = doc.output('blob');
  return URL.createObjectURL(blob);
}

/** Genera y descarga PDF Invoice PTC. */
export function generateInvoicePtcPdf(params) {
  buildInvoicePtcPdfDoc(params).save(invoicePtcPdfFileName(params));
}
